import React, { useRef, useEffect, useState } from 'react';

interface SignatureCanvasProps {
  onSignatureChange: (signatureDataUrl: string | null) => void;
  initialSignature?: string | null;
  disabled?: boolean;
  width?: number;
  height?: number;
}

/**
 * SignatureCanvas Component
 *
 * A canvas-based signature pad that allows users to draw their signature.
 * Uses HTML5 Canvas API for smooth drawing experience.
 *
 * Features:
 * - Touch and mouse support
 * - Clear button
 * - Returns signature as base64 PNG data URL
 * - Responsive sizing
 */
const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onSignatureChange,
  initialSignature = null,
  disabled = false,
  width = 400,
  height = 150,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  // Initialize canvas and load initial signature if provided
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas internal dimensions
    canvas.width = width;
    canvas.height = height;

    // Configure drawing style
    ctx.strokeStyle = '#232347';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }
  }, [width, height, initialSignature]);

  // Get position relative to canvas
  const getPosition = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      // Touch event
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      // Mouse event
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
  };

  // Start drawing
  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;

    e.preventDefault();
    setIsDrawing(true);
    const pos = getPosition(e);
    lastPosRef.current = pos;
  };

  // Draw line
  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;

    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !lastPosRef.current) return;

    const pos = getPosition(e);

    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPosRef.current = pos;
    setHasSignature(true);
  };

  // End drawing
  const handleEnd = () => {
    if (!isDrawing) return;

    setIsDrawing(false);
    lastPosRef.current = null;

    // Send signature data to parent
    const canvas = canvasRef.current;
    if (canvas && hasSignature) {
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChange(dataUrl);
    }
  };

  // Clear signature
  const handleClear = () => {
    if (disabled) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    // Clear and fill with white
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    setHasSignature(false);
    onSignatureChange(null);
  };

  return (
    <div className="signature-canvas-container">
      <div
        className={`relative border-2 rounded-lg overflow-hidden ${
          disabled
            ? 'border-gray-200 bg-gray-50'
            : 'border-[#B2D8C5] bg-white'
        }`}
      >
        <canvas
          ref={canvasRef}
          className={`w-full touch-none ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
          style={{ maxWidth: '100%', height: 'auto', aspectRatio: `${width}/${height}` }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />

        {/* Signature line */}
        {!hasSignature && !disabled && (
          <div className="absolute bottom-8 left-4 right-4 border-b border-gray-300 pointer-events-none">
            <span className="absolute -bottom-5 left-0 text-xs text-gray-400">
              Sign above this line
            </span>
          </div>
        )}

        {/* Disabled overlay */}
        {disabled && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/50">
            <span className="text-sm text-gray-500">Signature locked</span>
          </div>
        )}
      </div>

      {/* Clear button */}
      {!disabled && (
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-gray-500 hover:text-red-500 transition-colors"
          >
            Clear Signature
          </button>
        </div>
      )}
    </div>
  );
};

export default SignatureCanvas;
